import { gql } from '@apollo/client';

/**
 * User Groups (Group Security v3, collector model) — GraphQL operations for the open-api
 * `user-group.resolver` (research/04-feedback §7.2). Distinct from `security-groups.ts` (the v1
 * scope-group ops, superseded). "Workspace" == Team → the team-scope field is `teamId`; the UI
 * renders it as "Workspace". Enums are UPPERCASE on the wire (DEVICE/ASSET/…). Group visibility
 * (PUBLIC/PRIVATE) is deprecated — every group is account-wide, so that field is no longer selected.
 *
 * Response typing is done with hand-written interfaces at the call sites (no codegen in this repo).
 */

const USER_GROUP_LIST_FIELDS = gql`
  fragment UserGroupListFields on UserGroupListItem {
    _id
    accountId
    name
    description
    color
    isSystem
    teamId
    teamName
    roleIds
    memberCount
    resourceCount
    workspaceCount
    resourceCountByType {
      resourceType
      count
    }
    createdAt
    lastUpdatedDate
  }
`;

export const USER_GROUPS_QUERY = gql`
  query UserGroups($query: QueryUserGroupInput) {
    userGroups(query: $query) {
      ...UserGroupListFields
    }
  }
  ${USER_GROUP_LIST_FIELDS}
`;

export const USER_GROUP_DETAIL_QUERY = gql`
  query UserGroupDetail($id: String!) {
    userGroupDetail(id: $id) {
      _id
      accountId
      name
      description
      color
      teamId
      teamName
      roleIds
      createdBy
      createdAt
      lastUpdatedDate
      memberList {
        _id
        name
        email
        username
        hasMfa
      }
      invites {
        email
        name
        invitedAt
      }
      resourceList {
        resourceType
        resourceId
        resourceName
        resourcePath
        teamId
        teamName
        addedAt
      }
      workspaces {
        teamId
        teamName
        role
        homeFolders {
          type
          groupId
          path
          name
        }
        canEditHomePath
      }
    }
  }
`;

export const WORKSPACE_MEMBERS_QUERY = gql`
  query WorkspaceMembers($workspaceId: String!) {
    workspaceMembers(workspaceId: $workspaceId) {
      _id
      name
      email
      roles
      sources {
        type
        groupId
        groupName
        role
      }
    }
  }
`;

export const WORKSPACE_GROUPS_QUERY = gql`
  query WorkspaceGroups($workspaceId: String!) {
    workspaceGroups(workspaceId: $workspaceId) {
      groupId
      groupName
      role
      memberCount
      addedAt
      homeFolders {
        type
        groupId
        path
        name
      }
      canEditHomePath
    }
  }
`;

// Home-folder picker (§12 per-type): folders of ONE content type in a workspace, via the STANDARD
// `groups` resolver (not a special candidates API). Setting a group's home is admin-only, and admins
// bypass the folder read filter, so this lists ALL folders of that type — paginated, no 100-cap. Only
// the fields the dropdown needs are selected.
export const FOLDER_PICKER_GROUPS_QUERY = gql`
  query FolderPickerGroups($query: QueryGroupInput, $teamId: String, $first: Int) {
    groups(query: $query, teamId: $teamId, first: $first) {
      page {
        edges {
          node {
            _id
            name
            path
            teamId
          }
        }
      }
    }
  }
`;

export const USER_GROUP_RESOURCE_CANDIDATES_QUERY = gql`
  query UserGroupResourceCandidates(
    $teamId: String!
    $resourceType: UserGroupResourceType!
    $search: String
    $folderType: UserGroupResourceType
  ) {
    userGroupResourceCandidates(
      teamId: $teamId
      resourceType: $resourceType
      search: $search
      folderType: $folderType
    ) {
      resourceId
      name
      path
      teamId
      securityGroupIds
    }
  }
`;

// Model B: objects of a type a group actually grants (securityGroupIds ∋ group), paged + name search.
// Powers the reworked Resources tab. `securityGroupIds` is returned so the FE can remove a grant by
// pulling the group id out of the set and calling SET_RESOURCE_SECURITY_GROUPS. `isEdit` is THIS
// group's level on the row (§4 folder-side half) — it drives the tab's View/Edit column, whose writes
// go through SET_RESOURCE_GROUP_ACCESS (below).
export const GROUP_GRANTED_RESOURCES_QUERY = gql`
  query GroupGrantedResources(
    $groupId: String!
    $resourceType: UserGroupResourceType!
    $search: String
    $first: Int
    $offset: Int
    $folderType: UserGroupResourceType
  ) {
    groupGrantedResources(
      groupId: $groupId
      resourceType: $resourceType
      search: $search
      first: $first
      offset: $offset
      folderType: $folderType
    ) {
      total
      items {
        resourceType
        resourceId
        name
        path
        teamId
        teamName
        securityGroupIds
        isEdit
        folderType
      }
    }
  }
`;

export const GROUP_ROLE_USAGE_QUERY = gql`
  query GroupRoleUsage($roleIds: [String!]!) {
    groupRoleUsage(roleIds: $roleIds) {
      roleId
      count
    }
  }
`;

export const RESOURCE_GRANTING_GROUPS_QUERY = gql`
  query ResourceGrantingGroups($resourceType: UserGroupResourceType!, $resourceId: String!) {
    resourceGrantingGroups(resourceType: $resourceType, resourceId: $resourceId) {
      _id
      name
      color
      roleIds
      memberCount
    }
  }
`;

// --- Mutations ---

// Group Security §2 (T910/T912): create is ATOMIC — `input.grants[]` carries ≥1 workspace grant
// `{ teamId, role, homeFolders?, canEditHomePath?, autoCreateFolderTypes? }` and the group + its grants
// are written in one mutation (any grant failure fails the whole create, so a teamspace-less orphan
// group — a login-lockout hazard — is unreachable). No best-effort addGroupTo* follow-up loop. Grant
// fields live inside the `CreateUserGroupInput` object, so they're passed via `variables.input`, not
// enumerated in this document. `teamId === ROOT_TEAM_ID` is the account-level grant (backend routes it).
export const CREATE_USER_GROUP = gql`
  mutation CreateUserGroup($input: CreateUserGroupInput!) {
    createUserGroup(input: $input) {
      ...UserGroupListFields
    }
  }
  ${USER_GROUP_LIST_FIELDS}
`;

export const UPDATE_USER_GROUP = gql`
  mutation UpdateUserGroup($id: String!, $input: UpdateUserGroupInput!) {
    updateUserGroup(id: $id, input: $input) {
      ...UserGroupListFields
    }
  }
  ${USER_GROUP_LIST_FIELDS}
`;

export const DELETE_USER_GROUP = gql`
  mutation DeleteUserGroup($id: String!) {
    deleteUserGroup(id: $id) {
      success
      affectedMembers
      removedResourceGrants
      removedWorkspaceEntries
    }
  }
`;

export const ADD_USER_GROUP_MEMBERS = gql`
  mutation AddUserGroupMembers($groupId: String!, $userIds: [String!]!) {
    addUserGroupMembers(groupId: $groupId, userIds: $userIds) {
      count
    }
  }
`;

export const REMOVE_USER_GROUP_MEMBERS = gql`
  mutation RemoveUserGroupMembers($groupId: String!, $userIds: [String!]!) {
    removeUserGroupMembers(groupId: $groupId, userIds: $userIds) {
      count
    }
  }
`;

// §12 group-only — revoke a PENDING email invite off the group (userGroups.invites[]).
export const REVOKE_GROUP_INVITE = gql`
  mutation RevokeGroupInvite($groupId: String!, $email: String!) {
    revokeGroupInvite(groupId: $groupId, email: $email) {
      success
    }
  }
`;

export const ADD_USER_GROUP_RESOURCES = gql`
  mutation AddUserGroupResources($groupId: String!, $resources: [ResourceRefInput!]!) {
    addUserGroupResources(groupId: $groupId, resources: $resources) {
      count
    }
  }
`;

export const REMOVE_USER_GROUP_RESOURCES = gql`
  mutation RemoveUserGroupResources($groupId: String!, $resources: [ResourceRefInput!]!) {
    removeUserGroupResources(groupId: $groupId, resources: $resources) {
      count
    }
  }
`;

/**
 * Set which security groups a folder/item is shared to (Group Security Model B). Grants live on the
 * object's `securityGroupIds`. `groupIds`: null ⇒ inherit the folder's grant (unset), [] ⇒ shared to
 * nobody (per-item unshare), [ids] ⇒ explicit share. resourceType 'GROUP' = folder.
 */
export const SET_RESOURCE_SECURITY_GROUPS = gql`
  mutation SetResourceSecurityGroups(
    $resourceType: UserGroupResourceType!
    $resourceId: String!
    $groupIds: [String!]
  ) {
    setResourceSecurityGroups(
      resourceType: $resourceType
      resourceId: $resourceId
      groupIds: $groupIds
    ) {
      success
    }
  }
`;

/**
 * Set ONE group's access level (View/Edit) on a folder/item — the Resources tab's View/Edit column and
 * the add-resources "grant as" choice. UPSERTS this group's entry in the object's `securityGroupIds`
 * and leaves every other group's grant alone, so it is also the correct ADD path (no separate union
 * write is needed, and a new grant never lands at an unspecified level).
 *
 * §4 role cap: `isEdit` is only the FOLDER-SIDE half. Effective edit is
 * `isEdit && the group's role in that resource's teamspace allows edit` — setting Edit on a
 * viewer-role group persists the intent but still resolves view-only.
 */
export const SET_RESOURCE_GROUP_ACCESS = gql`
  mutation SetResourceGroupAccess(
    $resourceType: UserGroupResourceType!
    $resourceId: String!
    $groupId: String!
    $isEdit: Boolean!
  ) {
    setResourceGroupAccess(
      resourceType: $resourceType
      resourceId: $resourceId
      groupId: $groupId
      isEdit: $isEdit
    ) {
      success
    }
  }
`;

// Group Security §3 (T920/T921): the all-or-nothing `autoCreateHomeFolder: Boolean` is REPLACED by
// `autoCreateFolderTypes: [UserGroupResourceType!]` — only the listed content trees get a folder named
// after the group. `[]` (or omitted) ⇒ create nothing. The backend still accepts the boolean for one
// release (mapped to all five), but the FE no longer sends it.
export const ADD_GROUP_TO_WORKSPACE = gql`
  mutation AddGroupToWorkspace(
    $workspaceId: String!
    $groupId: String!
    $role: String!
    $homeFolders: [GroupHomeFolderInput!]
    $canEditHomePath: Boolean
    $autoCreateFolderTypes: [UserGroupResourceType!]
  ) {
    addGroupToWorkspace(
      workspaceId: $workspaceId
      groupId: $groupId
      role: $role
      homeFolders: $homeFolders
      canEditHomePath: $canEditHomePath
      autoCreateFolderTypes: $autoCreateFolderTypes
    ) {
      success
    }
  }
`;

export const UPDATE_GROUP_WORKSPACE_ROLE = gql`
  mutation UpdateGroupWorkspaceRole($workspaceId: String!, $groupId: String!, $role: String!) {
    updateGroupWorkspaceRole(workspaceId: $workspaceId, groupId: $groupId, role: $role) {
      success
    }
  }
`;

export const REMOVE_GROUP_FROM_WORKSPACE = gql`
  mutation RemoveGroupFromWorkspace($workspaceId: String!, $groupId: String!) {
    removeGroupFromWorkspace(workspaceId: $workspaceId, groupId: $groupId) {
      success
    }
  }
`;

// §3 (T920/T921) — same `autoCreateFolderTypes` replacement as addGroupToWorkspace above.
export const ADD_GROUP_TO_ACCOUNT = gql`
  mutation AddGroupToAccount(
    $groupId: String!
    $role: String!
    $homeFolders: [GroupHomeFolderInput!]
    $canEditHomePath: Boolean
    $autoCreateFolderTypes: [UserGroupResourceType!]
  ) {
    addGroupToAccount(
      groupId: $groupId
      role: $role
      homeFolders: $homeFolders
      canEditHomePath: $canEditHomePath
      autoCreateFolderTypes: $autoCreateFolderTypes
    ) {
      success
    }
  }
`;

export const REMOVE_GROUP_FROM_ACCOUNT = gql`
  mutation RemoveGroupFromAccount($groupId: String!) {
    removeGroupFromAccount(groupId: $groupId) {
      success
    }
  }
`;

/**
 * §7 (T960/T961) — retire the "turn on Groups" prompt. ACCOUNT-scoped, not per-user (spec §7): the
 * backend stamps `company.featureData.groupsPromo.dismissedAt`, so the prompt never returns for
 * anyone in the account. No args — the account comes from the auth context. Boolean result, matching
 * the other dismiss-a-prompt mutation shape (`dismissPlatformCleanup`).
 * The READ side needs no operation: `me.featureData` is the company's featureData as a JSON scalar,
 * so `groupsPromo` rides the existing ME_QUERY (see GroupsPromoBanner).
 */
export const DISMISS_GROUPS_PROMO = gql`
  mutation DismissGroupsPromo {
    dismissGroupsPromo
  }
`;
